/**
 * Workspace — the public workspace card, and its private settings sibling.
 * Also the home of the shared `Id` vocabulary and the `Actor` shape every
 * `entity<Actor>()` in this package binds against — declared here because
 * Workspace is the root every other tenant-scoped path hangs off, the same
 * way momo-starter exports its own `Id` from its own root entity file.
 *
 * Path: `workspaces/{orgId}` — public read; member update behind
 * `tickets.events.manage` + mfa.
 * Path: `workspaces/{orgId}/settings/private` — member read behind
 * `tickets.events.manage` + mfa; member write behind the same permission
 * EXCEPT `invoiceCounter` (server-write only — the receipt task's
 * transactional increment) and `revolut` (owner write behind
 * `workspace.credentials.manage` + mfa instead, with active mode callable-owned);
 * both masks are a hand fragment in `ican.extraMatches`.
 * Credential values live ciphertext at
 * `workspaces/{orgId}/integrations/{id}` (integration.entity.ts),
 * default-denied to every client — Admin SDK only.
 * Google Wallet holds no such doc: the runtime service account IS the
 * credential, so there is nothing to store or default-deny.
 */

import { entity } from "@mhmo91/ican";
import { firestore } from "@mhmo91/ican/firestore/binding";
import { LegalDocumentKindSchema } from "@momo/i18n";
import type { Timestamp } from "firebase/firestore";
import { z } from "zod";
import { BookingFeeSchema } from "./booking-fee.entity.ts";
import { IntegrationVerificationReasonSchema } from "./integration-save-failure.const.ts";
import type { IntegrationId } from "./integrations.const.ts";
import { PaymentModeSchema, PspProviderSchema } from "./order.entity.ts";

export {
	type OrganizationId,
	OrganizationIdSchema,
	RESERVED_ORGANIZATION_IDS,
} from "./organization-id.const.ts";

// ── Shared vocabulary ────────────────────────────────────────────────────────
// Every id in the model is this type, never a bare z.string().
export const Id = z.string().min(1);
export type Id = z.infer<typeof Id>;

// A sha-256 content hash, hex-encoded — 64 lowercase hex characters. The
// content address `legal.api.ts` (web) computes from each locale's
// published source text and every reader's blob fetch resolves against.
export const Sha256 = z.string().regex(/^[0-9a-f]{64}$/);
export type Sha256 = z.infer<typeof Sha256>;

/**
 * Actor — the caller shape every `entity<Actor>()` policy in this package
 * authors against.
 *
 * Not `User & { … }` (momo-starter's form): a caller here is a member OR an
 * anonymous buyer with no user document, so the actor carries only what both
 * genuinely have.
 *
 * `uid` — the caller's Firebase Auth uid — anonymous for a buyer, a member
 *   account otherwise.
 * `orgId` — the selected, grant-validated organization; context never grants authority.
 * `eventId` — the active event — the id `member('eventId', …)` resolves
 *   against; `null` outside an event context.
 * `mfa` — the `mfa: true` custom claim members carry once TOTP verifies;
 *   false/absent for a buyer's anonymous session.
 * `permissions` — assembled at session start from the caller's own grant →
 *   role lookup; empty for a buyer. `readonly string[]`, not
 *   `readonly Permission[]` — `hasPermission(...)` takes
 *   `FieldRef<readonly string[]>`, and a role's array is already validated
 *   against the closed catalog at the schema seam.
 * `workspaces` — the `{ [orgId]: roleId | null }` organization map every
 *   `member(...)` check reads; empty for a buyer.
 */
export type Actor = {
	readonly uid: string;
	readonly orgId: string;
	readonly eventId: string | null;
	readonly mfa: boolean;
	readonly permissions: readonly string[];
	readonly eventPermissions?: readonly string[];
	readonly memberships?: readonly string[];
	readonly workspaces: Readonly<Record<string, string | null>>;
};

/**
 * Workspace — the public workspace card at `workspaces/{orgId}`.
 *
 * Public read: the storefront renders brand/theme before any sign-in and
 * mounts the payment widget on provider+publicKey. `legal` is public too —
 * German Impressumspflicht requires the operating entity's legal name,
 * address, VAT id, register, and representative to be publicly disclosed on
 * the site, not gated behind sign-in. Everything else member-private lives
 * one level down on WorkspaceSettings.
 *
 * Fields justified:
 *   brand    — the storefront's identity; read before authentication, so it
 *              cannot live behind a member gate.
 *   flags    — per-workspace feature toggles the shell reads at boot.
 *   payment  — the PSP the widget mounts, plus the publishable half of its
 *              credential pair; both are public by construction.
 *   legal    — the seller identity a German receipt must carry (§14 UStG) —
 *              publicly disclosed by the same Impressumspflicht that governs
 *              the storefront itself.
 *   email    — the From address ticket and receipt emails send from; read
 *              alongside `brand.name` by the OTP/invite email senders, so it
 *              sits on the same public doc rather than forcing a second read.
 *   wallet   — whether this deployment issues Google/Apple Wallet passes at
 *              all, mirrored from `WorkspaceSettings.wallet`'s own
 *              issuer/pass-type presence. A CAPABILITY, never a credential —
 *              the mirror carries no issuer id, pass type id or team id, only
 *              the derived boolean, and it is already observable to any
 *              buyer who sees a wallet badge on their own order. It must be
 *              public: `settings/private` (where the underlying identifiers
 *              live) is read-gated behind `tickets.events.manage` + mfa, so
 *              an anonymous buyer can never resolve it — and the privacy
 *              policy that discloses Google/Apple as recipients is read by
 *              exactly that anonymous buyer. Written by a Firestore trigger
 *              mirroring `settings/private`, never by a client — see that
 *              trigger's own header for why a mirror rather than a
 *              same-write update from each wallet-config callable.
 *   legalContent — the content-addressed pointer to this deployment's own
 *              published Terms/Privacy override, one sha-256 per locale
 *              plus the operator's version stamp. Public for the same reason
 *              `legal` above is: an anonymous buyer must resolve a published
 *              override the same way they resolve the platform default, and
 *              this card is the read every visitor's storefront boot already
 *              performs, so the pointer costs zero additional reads. The
 *              locale text itself lives at Storage path
 *              `workspaces/{orgId}/legal/{kind}/{locale}/{sha256}.json`
 *              (`web/src/legal/legal.api.ts`), public and
 *              `Cache-Control: immutable` — the sha in the path is what
 *              makes that header honest, since a path can never come to
 *              name different bytes.
 *   ads      — the public ad-provider map, `meta.{pixelId, browserTag}`
 *              today. Public because the storefront reads BOTH facts at
 *              boot on the card read it already performs, before any
 *              sign-in and at zero additional reads: `pixelId` says a
 *              provider is configured, `browserTag` says whether any script
 *              may load — with it false the storefront loads nothing, asks
 *              nothing, and touches no visitor device. `browserTag`
 *              defaults to false, so the gate is fail-closed by
 *              construction: a card carrying no value for it parses as
 *              tag-off, which is the state that licenses nothing. Written
 *              only by the Admin SDK in saveIntegration's `register` hook,
 *              which states the operator's own choice on every save — no
 *              client write path, so no rule. The i18n legal builder
 *              derives its two conditional disclosures from the same pair:
 *              the Meta recipient row wherever a pixel is configured, the
 *              cookie line only where the browser tag runs.
 * Excluded:
 *   orgId       — the doc id is the org id; a mirrored field would drift.
 *   ownerId     — no owner-bootstrap write path; provisioning is scripted.
 *   createdAt   — no reader consumes the workspace's age.
 *   adSpendCeilingMinor — the bound on paid promotion lives at its own
 *               `settings/adSpendCeiling` singleton
 *               (ad-spend-ceiling.entity.ts). This card is read `if (true)`
 *               and the
 *               storefront holds a live listener on it for the whole life of
 *               every anonymous visit, so a field placed here is pushed
 *               continuously to every browser that ever opens the shop.
 */

/**
 * LegalContentPointerSchema — one legal-document kind's published override,
 * as a content address rather than the text itself: `version` is the
 * operator's own stamp, `de`/`en` are that locale's sha-256 over its source
 * text — the bytes living at Storage path
 * `workspaces/{orgId}/legal/{kind}/{locale}/{sha256}.json`. Shared between
 * `terms` and `privacy` below, the two kinds carrying the identical shape —
 * the same reuse `RevolutEnvStatusSchema` gives `sandbox`/`live`.
 */
export const LegalContentPointerSchema = z.object({
	version: z.string().min(1),
	de: Sha256,
	en: Sha256,
});
export type LegalContentPointer = z.infer<typeof LegalContentPointerSchema>;

/**
 * OperatorIdentitySchema — the legal identity of whoever operates a service a
 * person contracts with: an organization on its workspace card's `legal`
 * block, the deployment operator on `platform/legal` (platform.entity.ts).
 * contactEmail and phone are nullish for the workspace card's stored
 * shape; the `legal` field's description below states why.
 */
export const OperatorIdentitySchema = z.object({
	legalName: z.string().min(1),
	address: z.string().min(1),
	vatId: z.string().min(1),
	register: z.string().min(1),
	representative: z.string().min(1),
	contactEmail: z.string().email().nullish(),
	phone: z.string().min(1).nullish(),
});
export type OperatorIdentity = z.infer<typeof OperatorIdentitySchema>;

const SiteAssetSchema = z
	.string()
	.trim()
	.min(1)
	.refine(
		(value) => {
			if (!value.includes(":")) {
				return (
					/^[a-zA-Z0-9][a-zA-Z0-9._/-]*$/.test(value) &&
					!value
						.split("/")
						.some((segment) => segment === "." || segment === "..")
				);
			}
			try {
				const url = new URL(value);
				return url.protocol === "https:" && !url.username && !url.password;
			} catch {
				return false;
			}
		},
		{ message: "must be a public HTTPS URL or a Storage object path" },
	);

// Public settings used by runtime links, Maps and crawler-visible build output.
// Business identity is read from the existing brand/legal/email fields.
export const WorkspaceSiteSchema = z.object({
	// The domain controller supplies this override; site appearance and Maps
	// settings also exist while canonical links use the platform address.
	origin: z
		.string()
		.trim()
		.refine(
			(value) => {
				try {
					const url = new URL(value);
					return (
						url.protocol === "https:" &&
						(url.pathname === "/" ||
							/^\/o\/[a-zA-Z0-9_-]+\/?$/.test(url.pathname)) &&
						!url.search &&
						!url.hash &&
						!url.username &&
						!url.password
					);
				} catch {
					return false;
				}
			},
			{
				message:
					"must be an HTTPS address with only an optional organization path",
			},
		)
		.transform((value) => {
			const url = new URL(value);
			return `${url.origin}${url.pathname.replace(/\/$/, "")}`;
		})
		.optional(),
	mapsApiKey: z.string().trim().min(1).optional(),
	seo: z
		.object({
			title: z.string().trim().min(1).optional(),
			description: z.string().trim().min(1).optional(),
			locale: z.string().trim().min(1).optional(),
			socialImage: SiteAssetSchema.optional(),
			favicon: SiteAssetSchema.optional(),
			appleTouchIcon: SiteAssetSchema.optional(),
		})
		.optional(),
});
export type WorkspaceSite = z.infer<typeof WorkspaceSiteSchema>;

export const WorkspaceSchema = z.object({
	site: WorkspaceSiteSchema.optional(),
	brand: z.object({
		name: z
			.string()
			.min(1)
			.describe(
				"the event or venue name the storefront renders as its identity",
			),
		org: z
			.string()
			.nullish()
			.describe(
				"the operating organisation, when distinct from the brand name",
			),
		logo: z
			.string()
			.nullish()
			.describe("a Storage path or data URI for the storefront wordmark"),
		colors: z
			.record(z.string(), z.string())
			.nullish()
			.describe("theme token to CSS color, merged into the storefront theme"),
	}),
	flags: z
		.record(z.string(), z.boolean())
		.describe(
			"feature toggles members flip per workspace — e.g. scanner, embedWidget",
		),
	payment: z.object({
		provider: PspProviderSchema,
		mode: PaymentModeSchema.nullish().describe(
			"the active Revolut environment for new checkouts; absent until an environment is explicitly activated",
		),
		publicKey: z
			.string()
			.min(1)
			.nullish()
			.describe(
				"the active environment's publishable key — the widget’s public credential; the matching secret API key lives ciphertext on integrations/{revolutSandbox,revolutLive}, with the Secret Manager binding as the platform fallback. Absent while a fork awaits its PSP onboarding: the storefront’s wallet row collapses and the card path stands alone until a member fills it",
			),
	}),
	legal: OperatorIdentitySchema.nullish().describe(
		"the seller identity a German receipt must carry (§14 UStG) — publicly disclosed (Impressumspflicht). Nullish because a workspace is provisioned before an operator has necessarily decided it, and the Settings → Organization surface writes it at runtime; absence means not-yet-configured, never a placeholder, because a fabricated statutory disclosure is worse than a withheld one. Every reader guards: the storefront's Impressum surfaces render their identityUnavailable notice and invoice issuance refuses to number a document it cannot attribute. legalName/address/vatId/register/representative stay required on a present block — they were provisioned together, at the same moment, on every workspace card that has ever existed. contactEmail and phone are the exception: § 5 Abs. 1 Nr. 2 DDG's rapid-contact channel(s), added to this block after workspace cards were already live in the field, so a present block with neither still parses — an already-stored five-field card is not a validation failure, and every reader falls back to identityUnavailable-shaped absence the same way it already does for a null block. contactEmail is a distinct mailbox from email.sender below (the transactional From address, never a contact address a buyer or authority reaches a human through); phone is optional even once contactEmail is set, since an operator may rely on the email channel alone",
	),
	email: z.object({
		sender: z
			.string()
			.email()
			.describe("the From address ticket and receipt emails send from"),
	}),
	bookingFee: BookingFeeSchema.prefault({ amount: 0 }).describe(
		"the flat charge every event under this workspace adds once per order unless it states its own — resolved through resolveBookingFee (booking-fee.entity.ts), never read directly. Defaulted rather than nullish, and the default is a fee of zero: a workspace card written before the operator had any fee to set is a workspace charging nothing, which is a fee this schema can state, so there is no absent case for a pricing site to interpret. Cleared by setting the amount to zero, never by writing null — the field is on the public card the storefront prices an unauthenticated basket off, so a reader that had to treat absence as its own third case would be one more place the storefront and settle could disagree about a total",
	),
	wallet: z
		.object({
			googleEnabled: z
				.boolean()
				.nullish()
				.describe(
					"whether a Google Wallet issuer id is configured on WorkspaceSettings.wallet.googleIssuerId — presence only, never the id itself",
				),
			appleEnabled: z
				.boolean()
				.nullish()
				.describe(
					"whether BOTH an Apple pass type id and team id are configured on WorkspaceSettings.wallet — presence only, never either identifier",
				),
		})
		.nullish()
		.describe(
			"the public capability mirror of WorkspaceSettings.wallet, kept true by a Firestore trigger rather than hand-written by any client. Nullish at both levels like `legal` above: every workspace card already at rest predates this field, and the mirror trigger backfills it on its first write for that org — absence reads as 'not yet mirrored', which every consumer treats identically to 'not enabled' (deploymentProcessors's own NO_CAPABILITIES default), never as a reason to guess.",
		),
	legalContent: z
		.object({
			terms: LegalContentPointerSchema.nullish().describe(
				"the content-addressed pointer to this deployment's own published Terms of Service — nullish while the operator has never published an override for this kind",
			),
			privacy: LegalContentPointerSchema.nullish().describe(
				"the content-addressed pointer to this deployment's own published Privacy Policy — nullish while the operator has never published an override for this kind",
			),
		})
		.nullish()
		.describe(
			"the public, content-addressed pointer every storefront visitor's already-live workspace-card read resolves a published legal-document override through. Nullish at every level like `wallet` above: every workspace card already at rest predates this field, and every reader already falls back to the platform default (@momo/i18n's TERMS/PRIVACY) for whichever kind carries no pointer.",
		),
	ads: z
		.object({
			meta: z
				.object({
					// The format is Meta's, and is checked where an operator's value
					// enters (saveIntegration.action.ts) — never here. This card is
					// read by every anonymous visitor through a hard parse feeding a
					// module-scope boot subscription, so a stored value the schema
					// rejects is a storefront that stops rendering, not a retried
					// read. A vocabulary change at Meta must refuse the next save
					// and leave every card already at rest readable.
					pixelId: z
						.string()
						.min(1)
						.describe(
							"the Meta Pixel (dataset) id — public, never a credential; the browser tag initialises on it and the server conversion POST addresses it",
						),
					browserTag: z
						.boolean()
						.default(false)
						.describe(
							"whether the storefront also loads Meta's browser script — the storefront's own gate, read at boot, and defaulted off so a card carrying no value reads as tag-off rather than failing to parse. While it is false nothing third-party loads, no cookie is written and no visitor is asked anything; only its being true makes a consent prompt exist",
						),
				})
				.nullish()
				.describe(
					"Meta's row — present once a save's register hook has proved the access token reads this pixel, absent while the integration is unconfigured; a present row answers both questions a storefront boot asks of a provider, whether it is configured and whether its tag runs",
				),
		})
		.nullish()
		.describe(
			"the closed ad-provider map every visitor's storefront boot reads off this card — written only by the Admin SDK in saveIntegration's register hook, never by a client, and the key the legal builder derives each provider's disclosure from. Nullish at both levels like `wallet` above: every card at rest predates this field, and absence reads as no provider configured.",
		),
});
export type Workspace = z.infer<typeof WorkspaceSchema>;
// bookingFee carries a .default(), so a writer that omits it and lets
// WorkspaceSchema.parse supply the zero fee types its own literal against this
// input shape, not the output above — which demands the field the writer never
// sets. A literal that never parses (a fixture, a seed) states the field
// itself and stays on the output type.
export type WorkspaceInput = z.input<typeof WorkspaceSchema>;

// Public: the storefront renders brand and mounts the payment widget before
// any sign-in. Update needs the event-config permission plus the second
// factor. Neither rule touches `resource.data`, so `allow read` and
// `allow list` both emit.
export const Workspace = entity<Actor>()({
	schema: WorkspaceSchema,
	actions: ["read", "list"] as const,
	namespace: "tickets.workspace",
})
	.pipe(({ can, and }) => [
		can("read").when(() => and()),
		can("list").when(() => and()),
	])
	.adaptor(firestore({ hierarchicalGrants: true, path: "workspaces/{orgId}" }));

/**
 * WorkspaceSettings — the private singleton at
 * `workspaces/{orgId}/settings/private`.
 *
 * Member read behind tickets.events.manage + mfa. Two member-write masks
 * live in a hand fragment (`ican.extraMatches`) instead of a generated
 * `update` action: `wallet` under `tickets.events.manage`, and `revolut`
 * under `workspace.credentials.manage` — each
 * its own `allow update:` clause,
 * scoped by `affectedKeys().hasOnly([...])`, the same field-mask machinery
 * `events.availability` already uses to exclude one field from a generated
 * member-CRUD pattern. A generated `update` action's field mask
 * (`.writes(...)`) only pins fixed literal values per key — it cannot
 * express "any value, confined to this one key" for a variable-shaped
 * nested object like `revolut` (now itself carrying nested per-env
 * `sandbox`/`live` objects), so both member-write shapes are hand
 * fragments; `invoiceCounter` is excluded from both — server-write only
 * (the receipt task's transactional increment). No `update` action is
 * declared here, so nothing generated competes with either hand-written
 * clause.
 *
 * `legal` and `email.sender` live on the public Workspace card instead
 * (workspace.entity.ts's WorkspaceSchema) — Impressumspflicht requires the
 * seller identity to be publicly disclosed, not member-gated, so this
 * private singleton never carried the authoritative copy.
 *
 * Fields justified:
 *   wallet          — the Apple/Google pass issuer identifiers; each is
 *                     nullish because each tolerates absence pending its own
 *                     external provisioning (Apple Developer org enrollment,
 *                     Google Wallet publishing-access review has no
 *                     published SLA) — the same tolerance the config seam
 *                     already carries for this field (schema.ts's
 *                     `settings.wallet`).
 *   invoiceCounter  — the § 14 sequential number source.
 *   foundedBy       — the uid of the listed-domain account that founded
 *                     the organization; absent for one staff founded with an
 *                     invitation. The founded-once read: a uid some
 *                     organization names here has founded. Written only by
 *                     the founding (Admin SDK); no client update mask admits
 *                     it, so nobody can clear it to found again.
 *   revolut         — Revolut payment CONFIG and webhook identity, no
 *                     secret values (RevolutStatus): the active mode and
 *                     currency the money path resolves, each environment's
 *                     registered webhook identity, and the Apple Pay domain
 *                     with its registration stamp. The sk_/wsk_ values live
 *                     ciphertext on
 *                     integrations/{revolutSandbox,revolutLive}
 *                     (integration.entity.ts), default-denied to every
 *                     client.
 *   lettermint      — Lettermint delivery-webhook CONFIG, no secret values
 *                     (LettermintStatus): the route and delivery URL the
 *                     registration reconcile registers against, plus the
 *                     registration's own id. The tokens and every
 *                     registration's signing secret live ciphertext on
 *                     integrations/lettermint, default-denied to every
 *                     client.
 *   appleWallet     — the stored Apple Wallet signing certificate's own
 *                     expiry facts (AppleWalletStatus): its `notAfter` and
 *                     the expiry reconciler's alarm stamp. The certificate
 *                     bytes live ciphertext on integrations/appleWallet,
 *                     default-denied to every client.
 *   metaAds         — the Meta objects a paid promotion is built against
 *                     (MetaAdvertiser): the app, the ad account, the page
 *                     and the Instagram account. Configuration, no secret
 *                     values — the access token and app secret live
 *                     ciphertext on integrations/metaAds. It sits here
 *                     rather than on the public card because no
 *                     unauthenticated reader exists for any of it: the
 *                     storefront reads the pixel and the browser-tag choice
 *                     at boot and nothing else about this provider. That is
 *                     an audience argument, not a secrecy one — a Page is
 *                     visible on every ad it runs — but the ad account and
 *                     the app are this operator's billing and API
 *                     identities, and publishing them to every visitor buys
 *                     no reader anything.
 *   integrations    — per-registry-id verification status
 *                     (IntegrationStatus), keyed by IntegrationId —
 *                     verification facts only, never a credential value.
 *                     Written only by the Admin SDK (the saveIntegration
 *                     callable) — deliberately in NEITHER member-write mask
 *                     (`wallet`, `revolut`), so no client write ever touches
 *                     it; members read it through this doc's own read rule.
 *
 * Excluded:
 *   secrets      — every credential value lives ciphertext on the
 *                  default-denied `workspaces/{orgId}/integrations/{id}`
 *                  docs (integration.entity.ts), with the Secret Manager
 *                  bindings as the fresh-fork fallbacks — this
 *                  client-readable singleton never carries one.
 *   googleWallet — no config block: the runtime service account IS the
 *                  credential, the issuer id lives on
 *                  `wallet.googleIssuerId`, and its verification status
 *                  lives on `integrations.googleWallet`.
 *   per-provider verification facts — `integrations` above is their one
 *                  home.
 */
/**
 * RevolutEnvStatus — one environment's registered payment-webhook identity.
 *
 * Fields justified:
 *   webhookId  — the registration currently in effect — the key its signing
 *                secret files under in the integrations doc's `issued` map.
 *   webhookUrl — that registration's delivery URL — what the registration
 *                reconcile matches existing registrations against.
 *   publicKey  — THIS environment's own publishable key, persisted on every
 *                save (../../../firebase/src/events/checkout/psp/revolut.api.ts's
 *                registerRevolutWebhook) so an activation of THIS
 *                environment always has its OWN value to project onto the
 *                public Workspace card's `payment.publicKey` — never the
 *                sibling environment's, and never merely whatever the
 *                public card happened to carry from a prior activation.
 * Excluded:
 *   verification facts — `WorkspaceSettings.integrations` owns them.
 */
export const RevolutEnvStatusSchema = z.object({
	webhookId: z
		.string()
		.nullish()
		.describe(
			"the Revolut-issued id of the payment webhook currently registered for this environment",
		),
	webhookUrl: z
		.string()
		.nullish()
		.describe(
			"the URL Revolut currently delivers this environment's payment webhook events to",
		),
	publicKey: z
		.string()
		.min(1)
		.nullish()
		.describe(
			"this environment's own publishable key — the source an activation of THIS environment resolves the public Workspace card's payment.publicKey from when the save didn't submit a freshly-supplied one",
		),
});
export type RevolutEnvStatus = z.infer<typeof RevolutEnvStatusSchema>;

/**
 * RevolutStatus fields justified:
 *   mode                      — the active environment new checkouts read;
 *                               an already-prepared order settles under its
 *                               own stamped env regardless of a later flip.
 *   sandbox / live            — each environment's own registered
 *                               webhook identity (RevolutEnvStatus),
 *                               independent of the other and of `mode`, so
 *                               an operator configures and holds both at
 *                               once.
 *   currency                  — threaded into the money path (charge amount
 *                               currency, not just display); one currency
 *                               governs both environments.
 *   applePayDomain /
 *   applePayDomainRegistered  — the live-only Apple Pay domain-registration
 *                               step and its confirmed outcome.
 *   platformSandbox           — whether this organization's sandbox runs on
 *                               the deployment's platform/payments credential
 *                               rather than a key of its own: stamped true at
 *                               founding when that record exists, false once
 *                               the organization saves its own sandbox key.
 *                               The organization-facing reader (the
 *                               onboarding stepper) cannot read
 *                               platform/payments or the stored key, so this
 *                               is its one way to say which credential
 *                               checkout uses. Checkout itself never reads
 *                               it: the stored key decides.
 */
export const RevolutStatusSchema = z.object({
	mode: PaymentModeSchema.nullish().describe(
		"the active Revolut environment for new checkouts — governs the widget's publishable key and the host a new order is prepared on",
	),
	sandbox: RevolutEnvStatusSchema.nullish().describe(
		"the sandbox environment's registered payment-webhook identity — no secret values",
	),
	live: RevolutEnvStatusSchema.nullish().describe(
		"the live environment's registered payment-webhook identity — no secret values",
	),
	currency: z
		.string()
		.nullish()
		.describe(
			"the ISO-4217 currency new checkouts price and Revolut charges in — defaults to EUR when unset",
		),
	applePayDomain: z
		.string()
		.nullish()
		.describe(
			"the domain registered for Apple Pay on the live environment — live-only",
		),
	applePayDomainRegistered: z
		.boolean()
		.nullish()
		.describe(
			"whether Revolut confirmed the Apple Pay domain registration — live-only result",
		),
	platformSandbox: z
		.boolean()
		.nullish()
		.describe(
			"whether this organization's sandbox environment runs on the deployment's own sandbox credential because it has saved none of its own",
		),
});
export type RevolutStatus = z.infer<typeof RevolutStatusSchema>;

/**
 * LettermintStatus — the delivery-webhook configuration and registration
 * identity, no secret values: the route and delivery URL the registration
 * reconcile (registerLettermint) registers against, plus the registration
 * currently in effect. Every credential value lives ciphertext on the
 * default-denied `workspaces/{orgId}/integrations/lettermint` doc
 * (integration.entity.ts).
 *
 * Fields justified:
 *   routeId    — the Lettermint route the delivery webhook registers on;
 *                `route_id` is required on webhook create — with no route
 *                configured there is nothing to register against.
 *   webhookId  — the registration currently in effect — the key its signing
 *                secret files under in the integrations doc's `issued` map.
 *   webhookUrl — that registration's delivery URL — what the registration
 *                reconcile matches existing registrations against.
 * Excluded:
 *   verification facts — `WorkspaceSettings.integrations` owns them.
 */
export const LettermintStatusSchema = z.object({
	routeId: z
		.string()
		.nullish()
		.describe("the Lettermint route the delivery webhook is registered on"),
	webhookId: z
		.string()
		.nullish()
		.describe(
			"the Lettermint-issued id of the delivery-status webhook currently registered",
		),
	webhookUrl: z
		.string()
		.nullish()
		.describe(
			"the URL Lettermint currently delivers delivery-status events to",
		),
});
export type LettermintStatus = z.infer<typeof LettermintStatusSchema>;

/**
 * AppleWalletStatus — the stored Apple Wallet signing certificate's own
 * expiry facts: the certificate's `notAfter` and the expiry reconciler's
 * alarm stamp. The Pass Type ID certificate and WWDR G4 intermediate live
 * ciphertext on the default-denied
 * `workspaces/{orgId}/integrations/appleWallet` doc (integration.entity.ts).
 *
 * Fields justified:
 *   certificateExpiresAt      — read from the stored certificate's own
 *                               `notAfter`, never typed by the operator —
 *                               what the expiry alarm
 *                               (appleWalletExpiry.schedule.ts) reads.
 *   expiryAlarmedForExpiresAt — the certificateExpiresAt value that alarm
 *                               already fired for, so the same expiring
 *                               certificate never re-alarms on every tick;
 *                               a rotated certificate carries a new
 *                               certificateExpiresAt and so re-arms on its
 *                               own.
 * Excluded:
 *   applePassTypeId / appleTeamId — live at WorkspaceSettings.wallet.
 *   the certificate / WWDR PEM bytes — ciphertext on the integrations doc.
 *   verification facts — `WorkspaceSettings.integrations` owns them.
 */
export const AppleWalletStatusSchema = z.object({
	certificateExpiresAt: z
		.custom<Timestamp>()
		.nullish()
		.describe(
			"read from the stored certificate, not typed by the operator — what the expiry alarm reads",
		),
	expiryAlarmedForExpiresAt: z
		.custom<Timestamp>()
		.nullish()
		.describe(
			"the certificateExpiresAt value the expiry reconciler already alarmed for — written by appleWalletExpiry.schedule.ts so the same expiring certificate never re-alarms on every tick; a rotated certificate carries a new certificateExpiresAt and so re-arms on its own",
		),
});
export type AppleWalletStatus = z.infer<typeof AppleWalletStatusSchema>;

/**
 * MetaAdvertiser — the four Meta objects a paid promotion is built against:
 * the app the API is called through, the account it is billed to, and the
 * Facebook Page and Instagram account it is published as. Operator-typed
 * configuration, never a credential — the access token and app secret live
 * ciphertext on the default-denied `workspaces/{orgId}/integrations/metaAds`
 * doc (integration.entity.ts).
 *
 * The stored shape polices membership and nothing more. Each identifier's
 * FORMAT is Meta's, and is checked where an operator's value enters — the
 * save contract (saveIntegration.action.ts), which refuses a malformed one at
 * the parse rather than storing it. Here the same field is a plain non-empty
 * string on purpose: a format Meta changes would otherwise turn every read of
 * this singleton, and of the campaign documents that snapshot two of these
 * fields, into a whole-document parse failure on the money path. A value
 * already at rest stays readable whatever Meta does to its own vocabulary.
 *
 * campaign.entity.ts draws the two it snapshots at confirmation time from
 * this shape, so all three surfaces read one field set.
 *
 * Every field nullish: an operator configures a Meta integration in pieces,
 * and a partially-filled context is the ordinary state between the first save
 * and the last, not a defect. What refuses a half-configured context is the
 * ad builder, at the point it would otherwise spend money.
 *
 * Fields justified — each names one Meta object, and the reader that would
 * notice it missing is the ad builder, which addresses the ad account as a
 * path segment and names the page and Instagram account in the creative it
 * posts.
 * Excluded:
 *   pixelId          — the storefront reads it at boot on the public card
 *                      (WorkspaceSchema.ads.meta), so the card is its home;
 *                      a copy here would be a second answer.
 *   accessToken /
 *   appSecret        — credentials; ciphertext on the integrations doc.
 *   verification facts — `WorkspaceSettings.integrations` owns them.
 */
export const MetaAdvertiserSchema = z.object({
	appId: z
		.string()
		.min(1)
		.nullish()
		.describe(
			"the Meta app this workspace's calls are made through — the app the access token was minted under and whose secret signs the proof each call carries",
		),
	adAccountId: z
		.string()
		.min(1)
		.nullish()
		.describe(
			"the ad account campaigns are built in and billed to — the account whose currency names the unit every budget and spend figure counts in",
		),
	pageId: z
		.string()
		.min(1)
		.nullish()
		.describe(
			"the Facebook page an ad is published as — the ad identity a viewer sees it run under",
		),
	instagramAccountId: z
		.string()
		.min(1)
		.nullish()
		.describe(
			"the Instagram account an ad is published as, the one connected to the page",
		),
});
export type MetaAdvertiser = z.infer<typeof MetaAdvertiserSchema>;

/**
 * IntegrationStatus — one registry id's verification status, the value shape
 * of `WorkspaceSettings.integrations` below. Verification facts only, never
 * a credential value — the credential itself lives ciphertext on the
 * default-denied `workspaces/{orgId}/integrations/{id}` doc
 * (integration.entity.ts).
 *
 * Every field but serviceAccountEmail is required: an entry exists only
 * once the saveIntegration callable has run a verification for that id — a
 * FAILED verify writes this block too (lastVerifiedOk: false, nothing
 * stored), so the operator sees why their save was refused; only a PASSED
 * verify also stores the credential. A credential is proved by USING it, so
 * there is no saved-but-unverified state — and every save writes the whole
 * block at once. A delivery the provider permanently refuses is the same
 * proof by use: it rewrites the verification fields of an existing block
 * (never creates one), so a revoked grant stops reading verified.
 *
 * Fields justified:
 *   lastVerifiedOk     — whether the most recent verification passed.
 *   lastVerifiedAt     — when that verification ran; a check has authority
 *                        only as of this instant.
 *   lastVerifiedReason — the classified outcome behind lastVerifiedOk — the
 *                        closed five-member vocabulary
 *                        (IntegrationVerificationReasonSchema,
 *                        integration-save-failure.const.ts), `ok` on
 *                        success; never a raw provider message.
 *   lastVerifiedDetail — the provider's own message, verbatim free text,
 *                        empty when the provider said nothing — provider
 *                        specificity lives here so the reason vocabulary
 *                        never grows a per-provider member. A message, never
 *                        a credential value.
 *   changedByUid       — the member whose save produced this status.
 *   changedAt          — when that save landed.
 *   serviceAccountEmail — the runtime identity the most recent check ran as,
 *                        for an identity-based provider (Google Wallet) —
 *                        the address an operator grants access to on a
 *                        `notAuthorized`. Nullish: every other provider has
 *                        none, and the runtime identity is resolvable only
 *                        server-side, so no client can derive it. Written by
 *                        the save and by a delivery the provider refused.
 */
export const IntegrationStatusSchema = z.object({
	lastVerifiedOk: z
		.boolean()
		.describe(
			"whether the most recent verification of this integration's credential passed",
		),
	lastVerifiedAt: z
		.custom<Timestamp>()
		.describe("when the most recent verification ran"),
	lastVerifiedReason: IntegrationVerificationReasonSchema.describe(
		"the classified outcome behind lastVerifiedOk — ok on success; never a raw provider message",
	),
	lastVerifiedDetail: z
		.string()
		.describe(
			"the provider's own message for the most recent verification, verbatim free text — empty when the provider said nothing; never a credential value",
		),
	changedByUid: z
		.string()
		.min(1)
		.describe("the uid of the member whose save produced this status"),
	changedAt: z.custom<Timestamp>().describe("when that save landed"),
	serviceAccountEmail: z
		.string()
		.min(1)
		.nullish()
		.describe(
			"the runtime identity the most recent check ran as, for an identity-based provider — the address to grant access to; absent for every other provider",
		),
});
export type IntegrationStatus = z.infer<typeof IntegrationStatusSchema>;

/**
 * The status block as its one Admin-SDK write site constructs it:
 * `lastVerifiedAt`/`changedAt` are written as the server-timestamp sentinel
 * (`FieldValue.serverTimestamp()`), a class the stored `Timestamp` type does
 * not admit — so a literal typed against `IntegrationStatus` needs a
 * per-field double cast. This face swaps exactly those two fields for a
 * caller-instantiated sentinel type (`FieldValue` in the functions
 * workspace — firebase-admin stays unimported here), keeping every other
 * field statically checked against the schema.
 */
export type IntegrationStatusWrite<TServerTimestamp> = Omit<
	IntegrationStatus,
	"lastVerifiedAt" | "changedAt"
> & {
	lastVerifiedAt: TServerTimestamp;
	changedAt: TServerTimestamp;
};

export const WorkspaceSettingsSchema = z.object({
	foundedBy: Id.nullish().describe(
		"the uid of the listed-domain account that founded the organization; server-written only",
	),
	wallet: z.object({
		applePassTypeId: z.string().min(1).nullish(),
		appleTeamId: z.string().min(1).nullish(),
		googleIssuerId: z.string().min(1).nullish(),
	}),
	invoiceCounter: z.object({
		next: z
			.number()
			.int()
			.min(1)
			.describe(
				"the next § 14 invoice number to issue — gaps on retry are legal (UStAE 14.5 Abs. 10), skips logged",
			),
	}),
	revolut: RevolutStatusSchema.nullish().describe(
		"the Revolut payment configuration and webhook identity — carries no secret value",
	),
	lettermint: LettermintStatusSchema.nullish().describe(
		"the Lettermint delivery-webhook configuration and registration identity — carries no secret value",
	),
	appleWallet: AppleWalletStatusSchema.nullish().describe(
		"the stored Apple Wallet signing certificate's expiry facts — Admin-SDK-written; carries no secret value",
	),
	metaAds: MetaAdvertiserSchema.nullish().describe(
		"the Meta objects a paid promotion is built against — the app, the ad account, the page and the Instagram account. Written only by the Admin SDK on a save, and named by neither member-write mask, so no client write reaches it; carries no secret value, and is absent until a save supplies one",
	),
	integrations: z
		.partialRecord(z.custom<IntegrationId>(), IntegrationStatusSchema)
		.nullish()
		.describe(
			"per-registry-id verification status, keyed by IntegrationId — verification facts only, never a credential value; written only by the Admin SDK (the saveIntegration callable), absent until the first generic save",
		),
});
export type WorkspaceSettings = z.infer<typeof WorkspaceSettingsSchema>;

export const WorkspaceSettings = entity<Actor>()({
	schema: WorkspaceSettingsSchema,
	actions: ["read"] as const,
	namespace: "tickets.workspace",
})
	.pipe(({ can, hasPermission, and }) => [
		can("read").when((_data, me) =>
			and(
				hasPermission(me.permissions, "tickets.events.manage"),
				me.mfa.eq(true),
			),
		),
	])
	.adaptor(
		firestore({
			hierarchicalGrants: true,
			path: "workspaces/{orgId}/settings/private",
		}),
	);

/**
 * LegalVersion — the create-only archive of every published legal-document
 * version, at `workspaces/{orgId}/legalVersions/{versionId}`, sibling of the
 * public pointer on `WorkspaceSchema.legalContent` above: rollback and
 * producibility, never the live pointer a reader resolves through. Unlike
 * that public pointer, member-gated read — the archive carries
 * `publishedBy` (a member uid), which has no business reaching an anonymous
 * reader the way the public pointer does.
 *
 * There is no separate entity for the published document itself: each
 * locale's actual source text lives at a content-addressed Storage path
 * (`web/src/legal/legal.api.ts`), and the only Firestore-side
 * pointer to it is the `legalContent` field on the public `Workspace` card
 * above, governed by `Workspace`'s own `update` rule — there is no
 * `workspaces/{orgId}/settings/legal` document, and no rule of any kind
 * governs that path (default-deny holds for it exactly as for any path this
 * catalog never mentions).
 *
 * Fields justified:
 *   kind         — which document this archived version is (`terms` or
 *                  `privacy`) — `@momo/i18n`'s shared `LegalDocumentKindSchema`,
 *                  never a second enum, so the two packages never each keep
 *                  their own copy of the same two-value set.
 *   version      — the operator's own version stamp for this archived text.
 *   de / en      — both locales' source text as published, verbatim — an
 *                  archive entry is written only once a kind is fully
 *                  published (both locales present), so neither is nullish
 *                  here the way `LegalContentPointerSchema`'s fields are.
 *   publishedAt  — when this version was published.
 *   publishedBy  — the publishing member's uid — provenance the public
 *                  pointer deliberately never carries.
 *
 * `read`/`list` gated identically to `Workspace`'s own `update` (never
 * public, unlike the workspace card's own public read). No `update`, no
 * `delete` — an archive entry is immutable once written; default-deny
 * covers both the moment neither verb is declared below. `create` pins
 * `publishedBy` to the caller via `.writes(...)` — the same shape
 * `refund-batch.entity.ts`'s own `createdBy` pin uses — so the archive's
 * provenance of WHO published is genuine, not operator-suppliable.
 * `publishedAt` is NOT pinned to `.writes(...)`'s `serverTime` token:
 * `legal.api.ts` (the write seam) computes one `Timestamp.now()`
 * client-side and reuses it for both this archive entry and the workspace
 * card's `legalContent` pointer in the same batch, so the two writes
 * record the identical instant — a `serverTime` pin here would assert
 * equality against `request.time` (the server's own clock at rule
 * evaluation), which a client-supplied `Timestamp.now()` essentially never
 * matches exactly, and would reject every real publish. `kind`/`version`/
 * `de`/`en` stay unpinned too, since they are the operator's own content and
 * `.writes(...)`'s value map only ever asserts a fixed literal or a
 * `serverTime`/`me.uid` token per key, never "any value." No further
 * key-set closure is declared — `.writes(...)`'s `set` pattern has no
 * field-mask support at all (unlike its `update` pattern, which
 * auto-derives one from `diff(resource.data).affectedKeys()`), so closing
 * the top-level key set would need a hand fragment; every other create/set
 * write pattern in this codebase (`refund-batch.entity.ts` included) leaves
 * the top-level key set open the same way, and this entity follows that
 * same precedent rather than introducing the one exception.
 */
export const LegalVersionSchema = z.object({
	kind: LegalDocumentKindSchema,
	version: z.string().min(1),
	de: z.string().max(65536),
	en: z.string().max(65536),
	publishedAt: z.custom<Timestamp>(),
	publishedBy: z.string().min(1),
});
export type LegalVersion = z.infer<typeof LegalVersionSchema>;

export const LegalVersion = entity<Actor>()({
	schema: LegalVersionSchema,
	actions: ["create", "read", "list"] as const,
	namespace: "tickets.workspace",
})
	.pipe(({ can, hasPermission, and }) => [
		can("create")
			.when((data, me) =>
				and(
					hasPermission(me.permissions, "tickets.events.manage"),
					me.mfa.eq(true),
					data.publishedBy.eq(me.uid),
				),
			)
			.writes((w) => w.set({ publishedBy: "me.uid" })),
		can("read").when((_data, me) =>
			and(
				hasPermission(me.permissions, "tickets.events.manage"),
				me.mfa.eq(true),
			),
		),
		can("list").when((_data, me) =>
			and(
				hasPermission(me.permissions, "tickets.events.manage"),
				me.mfa.eq(true),
			),
		),
	])
	.adaptor(
		firestore({
			hierarchicalGrants: true,
			path: "workspaces/{orgId}/legalVersions/{versionId}",
		}),
	);
